import { getType, isStateTreeNode } from 'mobx-state-tree';
import Controller from './Controller';
import type Report from '../checks/Report';
import EditEventController from './EditEventController';
import EditObjectController from './EditObjectController';
import EditDependencyController from './EditDependencyController';
import EditMethodController from './EditMethodController';
import EditStateController from './EditStateController';
import EditTransitionController from './EditTransitionController';
import DeleteInheritanceController from './DeleteInheritanceController';
import type { IdOptionMap } from '../commands/types';
import type { IState } from '../../stores/model/metamodel/state';
import type { ITransition } from '../../stores/model/metamodel/transition';

/**
 * Controller to delete any element given its id.
 *
 * Used by the 'del' keyboard shortcut handler to delete the currently selected
 * element.
 *
 * Finds the element based on the id, determines its type and then delegates to
 * the appropriate controller.
 */
export default class DeleteAnyController extends Controller {
  isState(id: string): boolean {
    return !!this.modelVersion.content!.metamodel.getStateById(id);
  }

  isTransition(id: string): boolean {
    return !!this.modelVersion.content!.metamodel.getTransitionById(id);
  }

  canDelete(id: string): boolean {
    if (this.isState(id)) {
      const state = this.modelVersion.content!.metamodel.getStateById(id)!;
      if (state.fsm.type === 'DEFAULT') {
        return false;
      } else {
        return true;
      }
    } else if (this.isTransition(id)) {
      const transition = this.modelVersion.content!.metamodel.getTransitionById(id)!;
      if (transition.fsm.type === 'DEFAULT') {
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  isObjectType(id: string): boolean {
    return !!this.modelVersion.content!.metamodel.getObjectTypeById(id);
  }

  isInheritance(id: string): boolean {
    return !!this.modelVersion.content!.metamodel.getInheritanceById(id);
  }

  isInheritanceWithSpecialisedEvents(id: string): boolean {
    const inheritance = this.modelVersion.content!.metamodel.getInheritanceById(id);
    return inheritance != null && inheritance.specialisedEvents.length > 0;
  }

  async deleteAny(id: string, ...rest: unknown[]): Promise<Report | undefined> {
    const subject = this.modelVersion.content!.metamodel.findById(id);
    if (subject && isStateTreeNode(subject)) {
      const typeName = getType(subject).name;
      switch (typeName) {
        case 'EventType':
          return new EditEventController(this.modelVersion).deleteEvent(id);
        case 'ObjectType':
          // `rest` is the variadic delete options forwarded from the caller; the
          // runtime tuple matches EditObjectController.deleteObject's signature.
          return new EditObjectController(this.modelVersion).deleteObject(
            id,
            ...(rest as [boolean?, Record<string, IdOptionMap | undefined>?])
          );
        case 'Dependency':
          return new EditDependencyController(this.modelVersion).deleteDependency(id);
        case 'Method':
          return new EditMethodController(this.modelVersion).deleteMethod(id);
        case 'State': {
          // `getType(subject).name === 'State'` discriminates the union at
          // runtime; narrow it to IState so `.fsm` reads concretely. The MST
          // node and the hand-declared interface do not structurally overlap,
          // so the cast routes through `unknown`.
          const state = subject as unknown as IState;
          return new EditStateController(
            this.modelVersion,
            state.fsm.objectType.id,
            state.fsm.id
          ).deleteState(id);
        }
        case 'Transition': {
          const transition = subject as unknown as ITransition;
          return new EditTransitionController(
            this.modelVersion,
            transition.fsm.objectType.id,
            transition.fsm.id
          ).deleteTransition(id);
        }
        case 'Inheritance':
          return new DeleteInheritanceController(this.modelVersion).deleteInheritance(
            id,
            ...(rest as [(IdOptionMap | null)?])
          );
        case 'DataType':
          // there is currently no support for deleting a data type.
          break;
        default:
          break;
      }
    }
  }
}
